import difference from '@turf/difference';
import union from '@turf/union';
import type { Feature, MultiPolygon, Polygon, Position } from 'geojson';
import { polygonAreaAcres, polygonBboxFeet } from 'utils/geometry-measure';
import { formatNumber } from 'utils/number';
import type { Pieces } from 'components/geometry-editing';
import { CATEGORY_BY_AREA_TYPE } from 'components/create-area-panel';
import type {
  Bounds,
  EditMethod,
  EditorTool,
  GeometryPart,
  ReferenceCandidate,
  ReferenceMode,
  TraceTrack,
} from './types';
import type { AreaGeometryPart } from 'types/area-geometry';

/** The edit methods, as the mock words them. */
export const EDIT_METHODS: {
  id: EditMethod;
  title: string;
  description: string;
}[] = [
  {
    id: 'free-draw',
    title: 'User-drawn on map',
    description: 'Draft or active custom area',
  },
  {
    id: 'survey-description',
    title: 'Area from Survey',
    description: 'Survey-backed unit, tract, or AOI',
  },
  {
    id: 'upload',
    title: 'Upload shape file',
    description: 'Zipped shapefile or GeoJSON polygons',
  },
];

/** The construction tools, in the mock's order, with their labels. */
export const EDITOR_TOOLS: { id: EditorTool; label: string }[] = [
  { id: 'circle', label: 'Draw circle' },
  { id: 'polygon', label: 'Draw polygon' },
  { id: 'move', label: 'Move vertices' },
  { id: 'trace', label: 'Trace shown boundary' },
];

/** The upload block's hint inside the editor. */
export const EDITOR_UPLOAD_HINT =
  'Drop a zipped shapefile (.zip) or GeoJSON. Geometry is verified on upload — only Polygon / MultiPolygon files are accepted.';

/** The survey block's hint inside the editor. */
export const EDITOR_SURVEY_HINT =
  'Pick one or more survey areas (from the list or the map) and, optionally, a quarter call to carve down each tract. All rows combine into a single multi-polygon location.';

/** How each map tool works, for the toolbar's help, in toolbar order. */
export const DRAWING_TOOL_HELP: {
  id: EditorTool | 'snap' | 'landGrid' | 'undo';
  name: string;
  how: string;
}[] = [
  {
    id: 'circle',
    name: 'Draw circle',
    how: 'Click to set the center, move to size the radius, then click again to finish. Click an existing circle’s center to move it, or its edge to resize it.',
  },
  {
    id: 'polygon',
    name: 'Draw polygon',
    how: 'Click to drop points. Double-click, or click the first point, to close the shape. Backspace removes the last point.',
  },
  {
    id: 'move',
    name: 'Move vertices',
    how: 'Drag a vertex to reshape the boundary. Click any shape’s outline — rectangle, polygon, or circle — to add a vertex. Right-click or double-click a vertex to remove it.',
  },
  {
    id: 'trace',
    name: 'Trace shown boundary',
    how: 'Press on one of this area’s vertices, drag onto a shown area’s boundary or a county or state line and along it, then release to replace that stretch.',
  },
  {
    id: 'snap',
    name: 'Snap',
    how: 'When on, points snap to nearby shown area boundaries, otherwise to a fine grid.',
  },
  {
    id: 'landGrid',
    name: 'Land grid',
    how: 'When on, Snap and Trace also take the survey section lines (PLSS and Texas surveys), whichever tool is in use. Zoom in close for Trace to load them.',
  },
  {
    id: 'undo',
    name: 'Undo edit',
    how: 'Steps back one edit. Nothing is saved until you select Save.',
  },
];

/** The tools column's width: where it starts, and the least it and the map take. */
export const TOOLS_WIDTH = {
  storageKey: 'area-geometry-editor.toolsWidth',
  initial: 320,
  min: 288,
  mapMin: 360,
  /** The bar between them, which the row's width includes. */
  splitter: 8,
};

/**
 * A tools column width that leaves the map its minimum, whatever was asked for.
 *
 * @param {number} width - The width asked for, in px.
 * @param {number} total - The width the map and tools share, in px, splitter
 * excluded.
 * @return {number} The width to use, in whole px.
 */
export const clampToolsWidth = (width: number, total: number): number => {
  const max = Math.max(TOOLS_WIDTH.min, total - TOOLS_WIDTH.mapMin);
  return Math.round(Math.min(max, Math.max(TOOLS_WIDTH.min, width)));
};

/** The edited area, in blue whatever its type's colour, so it stands apart from the references. */
export const EDITED_AREA_COLOR = '#0f62fe';

/** Reference areas, in orange. */
export const REFERENCE_AREA_COLOR = '#ff832b';

/**
 * A geometry's outer rings, one piece each, as the shape tools edit them. Holes
 * are not edited; an area with one keeps it only until its part is redrawn
 * (see {@link piecesToGeometry}).
 *
 * @param {Polygon | MultiPolygon} geometry - The area's geometry.
 * @return {Pieces} Its outer rings.
 */
export const geometryToPieces = (
  geometry: Polygon | MultiPolygon | null | undefined
): Pieces => {
  if (!geometry) return [];
  const polygons =
    geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
  return polygons
    .map((rings) => rings[0])
    .filter((ring): ring is Position[] => !!ring && ring.length >= 4)
    .map((ring) => ring.slice(0, -1));
};

/**
 * The box around positions: west, south, east, north. A loop rather than
 * `Math.min(...)`, which overflows the call stack on an uploaded shape's
 * hundreds of thousands of vertices.
 *
 * @param {Position[]} points - The positions; at least one.
 * @return {[number, number, number, number]} The box.
 */
const positionsBox = (points: Position[]): [number, number, number, number] => {
  let [west, south] = points[0];
  let [east, north] = points[0];
  for (const [x, y] of points) {
    if (x < west) west = x;
    if (x > east) east = x;
    if (y < south) south = y;
    if (y > north) north = y;
  }
  return [west, south, east, north];
};

/**
 * A ring's box: west, south, east, north.
 *
 * @param {Position[]} piece - An open ring.
 * @return {number[]} The box.
 */
const pieceBox = (piece: Position[]): number[] => positionsBox(piece);

/**
 * Whether two boxes meet, edges included.
 *
 * @param {number[]} a - A box.
 * @param {number[]} b - Another.
 * @return {boolean} Whether they touch or overlap.
 */
const boxesMeet = (a: number[], b: number[]): boolean =>
  a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];

/**
 * The parts in groups that might meet: each group the parts whose boxes chain
 * together, in the order their first part comes.
 *
 * @param {Pieces} pieces - Open rings.
 * @return {Pieces[]} The groups.
 */
const meetingGroups = (pieces: Pieces): Pieces[] => {
  const boxes = pieces.map(pieceBox);
  const group = pieces.map((_, i) => i);
  /**
   * The group a part is in, following merges.
   *
   * @param {number} i - The part.
   * @return {number} Its group.
   */
  const root = (i: number): number => (group[i] === i ? i : root(group[i]));
  boxes.forEach((box, i) => {
    for (let j = 0; j < i; j += 1) {
      if (boxesMeet(box, boxes[j])) group[root(i)] = root(j);
    }
  });
  const groups = new Map<number, Pieces>();
  pieces.forEach((piece, i) => {
    const key = root(i);
    groups.set(key, [...(groups.get(key) ?? []), piece]);
  });
  return [...groups.values()];
};

/**
 * The parts with overlaps dissolved: parts that touch or overlap become one,
 * the rest stay separate. Holes the union leaves are dropped, as the tools edit
 * outer rings only. Parts that meet nothing come back as they were, the very
 * rings, so a part left as opened still keeps its holes on save. If a union
 * fails, its parts come back as they were.
 *
 * @param {Pieces} pieces - Open rings.
 * @return {Pieces} The merged parts.
 */
export const mergePieces = (pieces: Pieces): Pieces => {
  const valid = pieces.filter((piece) => piece.length >= 3);
  if (valid.length < 2) return valid;
  return meetingGroups(valid).flatMap((group) => {
    if (group.length < 2) return group;
    try {
      const merged = group
        .slice(1)
        .reduce<Feature<Polygon | MultiPolygon> | Polygon | null>(
          (sum, piece) => sum && union(sum, pieceToPolygon(piece)),
          pieceToPolygon(group[0])
        );
      if (!merged) return group;
      const result = geometryToPieces(
        'geometry' in merged ? merged.geometry : merged
      );
      // Boxes that met while the shapes didn't: nothing merged, so nothing changes.
      return result.length === group.length ? group : result;
    } catch {
      return group;
    }
  });
};

/**
 * The parts with a shape cut out of them: what Remove original area keeps of
 * the drawn parts. As with merging, holes the cut leaves are dropped. If the cut
 * fails, the parts come back as they were.
 *
 * @param {Pieces} pieces - Open rings.
 * @param {Polygon | MultiPolygon} removed - The shape to cut out.
 * @return {Pieces} What is left of the parts.
 */
export const subtractGeometry = (
  pieces: Pieces,
  removed: Polygon | MultiPolygon
): Pieces => {
  try {
    return pieces
      .filter((piece) => piece.length >= 3)
      .flatMap((piece) => {
        const rest = difference(pieceToPolygon(piece), removed);
        return rest ? geometryToPieces(rest.geometry) : [];
      });
  } catch {
    return pieces;
  }
};

/**
 * Pieces back to closed rings.
 *
 * @param {Pieces} pieces - Open rings.
 * @return {Position[][]} Closed rings.
 */
export const closeRings = (pieces: Pieces): Position[][] =>
  pieces.map((piece) => [...piece, piece[0]]);

/**
 * A piece as a polygon.
 *
 * @param {Position[]} piece - An open ring.
 * @return {Polygon} The polygon.
 */
export const pieceToPolygon = (piece: Position[]): Polygon => ({
  type: 'Polygon',
  coordinates: closeRings([piece]),
});

/**
 * A closed polygon ring as a piece.
 *
 * @param {Polygon} polygon - The polygon.
 * @return {Position[]} Its outer ring, open.
 */
export const polygonToPiece = (polygon: Polygon): Position[] =>
  polygon.coordinates[0].slice(0, -1);

/**
 * Whether an open ring is the same ring as a closed one, vertex for vertex.
 *
 * @param {Position[]} piece - An open ring.
 * @param {Position[]} ring - A closed ring.
 * @return {boolean} True when they match.
 */
const sameRing = (piece: Position[], ring: Position[]): boolean =>
  piece.length === ring.length - 1 &&
  piece.every(
    (position, index) =>
      position[0] === ring[index][0] && position[1] === ring[index][1]
  );

/**
 * The parts as the geometry the draft saves: one polygon, or a multi-part one.
 * A part whose outer ring is unchanged from the area as it opened keeps that
 * part's holes; a part that was redrawn has none, as the tools edit outer rings
 * only.
 *
 * @param {Pieces} pieces - The parts.
 * @param {Polygon | MultiPolygon | null} [original] - The area as it opened.
 * @return {Polygon | MultiPolygon} The geometry.
 */
export const piecesToGeometry = (
  pieces: Pieces,
  original?: Polygon | MultiPolygon | null
): Polygon | MultiPolygon => {
  const originalParts = !original
    ? []
    : original.type === 'Polygon'
      ? [original.coordinates]
      : original.coordinates;
  const polygons = pieces.map((piece) => {
    const [outer] = closeRings([piece]);
    const kept = originalParts.find(
      (rings) => rings.length > 1 && sameRing(piece, rings[0])
    );
    return kept ? [outer, ...kept.slice(1)] : [outer];
  });
  return polygons.length === 1
    ? { type: 'Polygon', coordinates: polygons[0] }
    : { type: 'MultiPolygon', coordinates: polygons };
};

/**
 * A whole number, grouped.
 *
 * @param {number} value - The number.
 * @return {string} "1,320".
 */
const wholeNumber = (value: number): string => formatNumber(value, 0, 0);

/**
 * A part's bounding box in feet and its acreage, measured on the client.
 *
 * @param {Position[]} piece - An open ring.
 * @return {{ widthFt: number, heightFt: number, acres: number }} Its size.
 */
const measurePiece = (piece: Position[]) => {
  const polygon = pieceToPolygon(piece);
  return { ...polygonBboxFeet(polygon), acres: polygonAreaAcres(polygon) };
};

/**
 * "Areas in this geometry" rows: each part's size, as the catalog lists drawn
 * polygons. The server's figures are used when it measured these parts; the
 * client measures them while drawn edits stand.
 *
 * @param {Pieces} pieces - The parts.
 * @param {AreaGeometryPart[] | null} figures - The server's measure of the same parts.
 * @return {GeometryPart[]} The rows.
 */
export const partRows = (
  pieces: Pieces,
  figures: AreaGeometryPart[] | null = null
): GeometryPart[] =>
  pieces.map((piece, index) => {
    const size = figures?.[index] ?? measurePiece(piece);
    return {
      index,
      label: `${wholeNumber(size.widthFt)} ft × ${wholeNumber(size.heightFt)} ft (${wholeNumber(
        size.acres
      )} acres)`,
    };
  });

/**
 * Whether the map tools work: not in Area from Survey, and not while saving.
 *
 * @param {EditMethod} method - The edit method.
 * @param {boolean} saving - Whether a save is in flight.
 * @return {boolean} True when the tools work.
 */
export const editorToolsEnabled = (
  method: EditMethod,
  saving: boolean
): boolean => method !== 'survey-description' && !saving;

/**
 * A boundary's Gross Acres, summed over its parts (outer rings, as the part rows
 * measure them).
 *
 * @param {Polygon | MultiPolygon} geometry - The boundary.
 * @return {number} Acres.
 */
export const geometryAcres = (geometry: Polygon | MultiPolygon): number =>
  geometry.type === 'Polygon'
    ? polygonAreaAcres(geometry)
    : geometry.coordinates.reduce(
        (sum, coordinates) =>
          sum + polygonAreaAcres({ type: 'Polygon', coordinates }),
        0
      );

/**
 * The bounds of every part, for Zoom to area.
 *
 * @param {Pieces} pieces - The parts.
 * @return {[number, number, number, number] | null} West, south, east, north.
 */
export const piecesBounds = (
  pieces: Pieces
): [number, number, number, number] | null => {
  const points = pieces.flat();
  return points.length === 0 ? null : positionsBox(points);
};

/**
 * Whether two extents overlap.
 *
 * @param {Bounds} a - One extent.
 * @param {Bounds} b - The other.
 * @return {boolean} True when they touch or overlap.
 */
export const boundsIntersect = (a: Bounds, b: Bounds): boolean =>
  a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];

/**
 * Reference rows nearest the edited area first, by the centres of their extents.
 * Rows with no extent go last, in the order they came.
 *
 * @param {ReferenceCandidate[]} items - The rows.
 * @param {Bounds | null} from - The edited area's extent.
 * @return {ReferenceCandidate[]} The rows, nearest first.
 */
export const sortByDistance = (
  items: ReferenceCandidate[],
  from: Bounds | null
): ReferenceCandidate[] => {
  if (!from) return items;
  const x = (from[0] + from[2]) / 2;
  const y = (from[1] + from[3]) / 2;
  // Degrees of longitude shrink away from the equator.
  const scale = Math.cos((y * Math.PI) / 180);
  /**
   * How far a row's centre lies from the edited area's.
   *
   * @param {ReferenceCandidate} item - The row.
   * @return {number} The distance, in scaled degrees; Infinity with no extent.
   */
  const distance = ({ extent }: ReferenceCandidate) =>
    extent
      ? Math.hypot(
          ((extent[0] + extent[2]) / 2 - x) * scale,
          (extent[1] + extent[3]) / 2 - y
        )
      : Infinity;
  return items
    .map((item, index) => ({ item, index, d: distance(item) }))
    .sort((a, b) => a.d - b.d || a.index - b.index)
    .map(({ item }) => item);
};

/**
 * The type the in-view search narrows to: the edited area's in "Same type", none
 * otherwise.
 *
 * @param {ReferenceMode} mode - Filter areas.
 * @param {string} category - The edited area's category.
 * @return {string | null} The category to search, or null for every type.
 */
export const inViewCategory = (
  mode: ReferenceMode,
  category: string
): string | null => (mode === 'same-type' ? category : null);

/** How close (px) a click must land to an edge to insert a vertex there. */
export const EDGE_TOLERANCE_PX = 6;

/** How close (px) a click must land to the first vertex to close a polygon. */
export const CLOSE_TOLERANCE_PX = 10;

/**
 * The edge under a screen point, and where on it, for inserting a vertex.
 *
 * @param {Function} project - Map projection, lng/lat to screen px.
 * @param {Pieces} pieces - The parts.
 * @param {{ x: number, y: number }} point - The pointer.
 * @param {number} tolerance - How near counts, in px.
 * @return {{ p: number, i: number, t: number } | null} The part, the vertex the
 * edge starts at, and how far along it (0 to 1); null when nothing is near.
 */
export const edgeAt = (
  project: (position: Position) => { x: number; y: number },
  pieces: Pieces,
  point: { x: number; y: number },
  tolerance: number = EDGE_TOLERANCE_PX
): { p: number; i: number; t: number } | null => {
  let best: { p: number; i: number; t: number; d: number } | null = null;
  pieces.forEach((piece, p) => {
    piece.forEach((start, i) => {
      const a = project(start);
      const b = project(piece[(i + 1) % piece.length]);
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const length = dx * dx + dy * dy;
      const t =
        length === 0
          ? 0
          : Math.max(
              0,
              Math.min(
                1,
                ((point.x - a.x) * dx + (point.y - a.y) * dy) / length
              )
            );
      const d = Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy));
      if (d <= tolerance && (!best || d < best.d)) best = { p, i, t, d };
    });
  });
  if (!best) return null;
  const { p, i, t } = best;
  return { p, i, t };
};

/**
 * The area type a category is created as, as the bar names it ("Areas of
 * Interest" for AOI); an unknown category reads as itself.
 *
 * @param {string} category - The stored category.
 * @return {string} Its label.
 */
export const areaTypeLabel = (category: string): string =>
  Object.entries(CATEGORY_BY_AREA_TYPE).find(
    ([, value]) => value === category
  )?.[0] ?? category;

/**
 * Whether a reference row lies outside the edit map's view: by its extent when
 * the row has one, otherwise by the in-view search. Nothing is out of view
 * before the map reports its extent.
 *
 * @param {ReferenceCandidate} candidate - The row's area.
 * @param {Bounds | null} viewBounds - The map's extent.
 * @param {ReadonlySet<number>} inViewIds - The areas the in-view search found.
 * @return {boolean} True when out of view.
 */
export const isOutOfView = (
  candidate: ReferenceCandidate,
  viewBounds: Bounds | null,
  inViewIds: ReadonlySet<number>
): boolean => {
  if (!viewBounds) return false;
  if (candidate.extent) return !boundsIntersect(candidate.extent, viewBounds);
  return !inViewIds.has(candidate.id);
};

/**
 * What the empty reference table says.
 *
 * @param {boolean} searching - Whether something is typed.
 * @param {ReferenceMode} mode - Filter areas.
 * @return {string} The message.
 */
export const referenceEmptyText = (
  searching: boolean,
  mode: ReferenceMode
): string => {
  if (searching) return 'No areas match.';
  return mode === 'same-type'
    ? 'No other areas of this type in view.'
    : 'No other areas in this view.';
};

/**
 * Filter areas: the areas added by hand, or those in view of the edited area's
 * type, which "Same type" names.
 *
 * @param {string} category - The edited area's category.
 * @return {Array} The modes with their labels.
 */
export const referenceModes = (
  category: string
): { id: ReferenceMode; label: string }[] => [
  { id: 'selected', label: 'Selected areas only' },
  { id: 'same-type', label: `Same type (${areaTypeLabel(category)})` },
];

/**
 * A closed ring laid out by arc length, in degrees as the mock measures it.
 *
 * @param {Position[]} ring - A closed ring.
 * @return {TraceTrack} The track.
 */
export const buildTrack = (ring: Position[]): TraceTrack => {
  const cum = [0];
  for (let n = 1; n < ring.length; n += 1) {
    cum.push(
      cum[n - 1] +
        Math.hypot(ring[n][0] - ring[n - 1][0], ring[n][1] - ring[n - 1][1])
    );
  }
  return { positions: ring, cum, total: cum[cum.length - 1] };
};

/**
 * A number wrapped into [0, total).
 *
 * @param {number} value - The number.
 * @param {number} total - The period.
 * @return {number} The wrapped value.
 */
const wrap = (value: number, total: number): number =>
  ((value % total) + total) % total;

/**
 * The nearest point on a track: its arc position, its squared distance and where
 * it is.
 *
 * @param {TraceTrack} track - The track.
 * @param {Position} point - The pointer.
 * @return {{ param: number, distance: number, position: Position }} The nearest.
 */
export const nearestOnTrack = (
  track: TraceTrack,
  point: Position
): { param: number; distance: number; position: Position } => {
  let best = { param: 0, distance: Infinity, position: track.positions[0] };
  for (let r = 0; r < track.positions.length - 1; r += 1) {
    const a = track.positions[r];
    const b = track.positions[r + 1];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const length = dx * dx + dy * dy;
    const t =
      length === 0
        ? 0
        : Math.max(
            0,
            Math.min(
              1,
              ((point[0] - a[0]) * dx + (point[1] - a[1]) * dy) / length
            )
          );
    const position: Position = [a[0] + t * dx, a[1] + t * dy];
    const distance =
      (point[0] - position[0]) ** 2 + (point[1] - position[1]) ** 2;
    if (distance < best.distance) {
      best = {
        param:
          track.cum[r] + Math.hypot(position[0] - a[0], position[1] - a[1]),
        distance,
        position,
      };
    }
  }
  return best;
};

/**
 * The point a given arc position falls on.
 *
 * @param {TraceTrack} track - The track.
 * @param {number} param - Arc position, wrapped round the ring.
 * @return {Position} The point.
 */
export const pointOnTrack = (track: TraceTrack, param: number): Position => {
  const at = wrap(param, track.total);
  for (let n = 0; n < track.positions.length - 1; n += 1) {
    if (at <= track.cum[n + 1]) {
      const span = track.cum[n + 1] - track.cum[n];
      const t = span === 0 ? 0 : (at - track.cum[n]) / span;
      const a = track.positions[n];
      const b = track.positions[n + 1];
      return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    }
  }
  return [...track.positions[track.positions.length - 1]];
};

/**
 * The ring's own vertices passed between an arc position and a run along it, in
 * the order they are passed. A negative run goes the other way round.
 *
 * @param {TraceTrack} track - The track.
 * @param {number} start - Where the run starts.
 * @param {number} run - How far it goes, signed.
 * @return {Position[]} The vertices passed.
 */
export const verticesAlong = (
  track: TraceTrack,
  start: number,
  run: number
): Position[] => {
  const length = Math.abs(run);
  const direction = Math.sign(run);
  if (length === 0 || direction === 0) return [];
  const passed: { d: number; position: Position }[] = [];
  for (let n = 0; n < track.positions.length - 1; n += 1) {
    const d = wrap(
      direction > 0 ? track.cum[n] - start : start - track.cum[n],
      track.total
    );
    if (d > 1e-9 && d < length) {
      passed.push({ d, position: [...track.positions[n]] });
    }
  }
  passed.sort((a, b) => a.d - b.d);
  return passed.map((entry) => entry.position);
};

/**
 * Whether two segments cross, touching at an end or not.
 *
 * @param {Position} a - The first segment's start.
 * @param {Position} b - Its end.
 * @param {Position} c - The second segment's start.
 * @param {Position} d - Its end.
 * @return {boolean} True when they meet.
 */
const segmentsCross = (
  a: Position,
  b: Position,
  c: Position,
  d: Position
): boolean => {
  /**
   * Which side of the line p→q the point r lies: -1, 0 on it, or 1.
   *
   * @param {Position} p - The line's start.
   * @param {Position} q - Its end.
   * @param {Position} r - The point.
   * @return {number} The side.
   */
  const side = (p: Position, q: Position, r: Position) =>
    Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]));
  return (
    side(a, b, c) !== side(a, b, d) &&
    side(c, d, a) !== side(c, d, b) &&
    side(a, b, c) !== 0 &&
    side(c, d, a) !== 0
  );
};

/**
 * Whether an open ring's edges cross one another, which makes a bow-tie whose
 * area nets out small.
 *
 * @param {Position[]} way - The ring, open.
 * @return {boolean} True when two edges that don't share a vertex cross.
 */
export const crossesItself = (way: Position[]): boolean => {
  const n = way.length;
  for (let i = 0; i < n; i += 1) {
    for (let j = i + 2; j < n; j += 1) {
      if (i === 0 && j === n - 1) continue;
      if (segmentsCross(way[i], way[(i + 1) % n], way[j], way[(j + 1) % n])) {
        return true;
      }
    }
  }
  return false;
};

/**
 * The two ways a run can close back onto its part: on along the ring from where
 * it ends, or back the other way. The shorter way round comes first.
 *
 * @param {TraceTrack} track - The part's ring.
 * @param {number} from - Where the grabbed vertex sits on it.
 * @param {Position[]} run - The traced positions, the grabbed vertex first.
 * @return {Position[][]} Both ways, open.
 */
const waysRound = (
  track: TraceTrack,
  from: number,
  run: Position[]
): Position[][] => {
  const to = nearestOnTrack(track, run[run.length - 1]).param;
  const forward = wrap(to - from, track.total);
  const back = track.total - forward;
  const viaTo = [...run, ...verticesAlong(track, to, back)];
  const viaFrom = [...run, ...verticesAlong(track, from, forward).reverse()];
  return forward <= back ? [viaTo, viaFrom] : [viaFrom, viaTo];
};

/**
 * The area a way round encloses.
 *
 * @param {Position[]} way - The part, open.
 * @return {number} Its acres; 0 when it is degenerate.
 */
const wayAcres = (way: Position[]): number =>
  way.length < 3 ? 0 : polygonAreaAcres(pieceToPolygon(way));

/** How many of a run's points a close may drop, at either end, to avoid a bow-tie. */
const TRIM_LIMIT = 8;

/**
 * Puts a traced run into a part in place of the vertex it started from, then
 * closes back to that vertex's ring whichever way round keeps the larger area, so
 * the trace drops the smaller side rather than the shorter one, and the shared
 * edge is the reference's own vertices on both areas.
 *
 * A way round that crosses itself is never kept: its area nets out small, so it
 * would beat the real shape only by accident. When a run ends just past a corner,
 * on the far side of the line it came up, every way that keeps any of the part's
 * other corners crosses, and the one clean way is a sliver along the run; the
 * run's last points are then dropped, a few at most, until a way that keeps the
 * part closes cleanly. A run whose first points step across the part's own edge,
 * tracing a boundary that all but lies on it, crosses the same way at its start;
 * the grabbed vertex and the points after it are dropped likewise.
 *
 * @param {Position[]} piece - The part, open.
 * @param {number} grabbed - The vertex the trace started from.
 * @param {Position[]} run - The traced positions, the grabbed vertex first.
 * @return {Position[] | null} The new part, open; null when it would be degenerate.
 */
export const closeTrace = (
  piece: Position[],
  grabbed: number,
  run: Position[]
): Position[] | null => {
  const track = buildTrack([...piece, piece[0]]);
  if (track.total === 0 || run.length < 2) return null;
  const from = track.cum[grabbed];
  /**
   * The larger of some ways, the first on a tie.
   *
   * @param {Position[][]} ways - The ways, the shorter round first.
   * @return {Position[]} The larger.
   */
  const larger = (ways: Position[][]) =>
    ways.reduce((best, way) => (wayAcres(way) > wayAcres(best) ? way : best));
  /**
   * The ways that close a run without crossing themselves.
   *
   * @param {Position[]} part - The run, or the start of it.
   * @return {Position[][]} The clean ways.
   */
  const clean = (part: Position[]) =>
    waysRound(track, from, part).filter(
      (way) => way.length >= 3 && !crossesItself(way)
    );
  const full = clean(run);
  /**
   * The clean ways that keep some of the part's other corners once `trim` of the
   * run's points are dropped, split every way between its start and end.
   *
   * @param {number} trim - How many points to drop.
   * @return {Position[][]} Those ways.
   */
  const keepingAfter = (trim: number) =>
    Array.from({ length: trim + 1 }, (_, start) =>
      run.slice(start, run.length - (trim - start))
    )
      .filter((part) => part.length >= 2)
      .flatMap((part) =>
        // A way longer than its run keeps some of the part's other corners.
        (trim === 0 ? full : clean(part)).filter(
          (way) => way.length > part.length
        )
      );
  let kept: Position[] | null = null;
  // Fewest points dropped first, from the end before the start.
  for (let trim = 0; trim <= TRIM_LIMIT && !kept; trim += 1) {
    const keeping = keepingAfter(trim);
    if (keeping.length > 0) kept = larger(keeping);
  }
  if (!kept && full.length > 0) kept = larger(full);
  // Every way crossed: the larger of the full run's, as before.
  kept ??= larger(waysRound(track, from, run));
  return kept.length < 3 ? null : kept.map((position) => [...position]);
};
